import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { 
  ArrowRight, Building2, Cloud, Sparkles, Zap, 
  Shield, TrendingUp, Users, Layers, Globe, CheckCircle
} from 'lucide-react';

const SolutionsPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const solutions = [
    {
      id: 'enterprise',
      title: 'Enterprise Solutions',
      description: 'Comprehensive enterprise-grade solutions that scale with your business and streamline operations.',
      icon: Building2,
      color: 'from-blue-500 to-indigo-600',
      features: ['ERP Integration', 'Business Process Automation', 'Custom Enterprise Apps', 'Legacy Modernization']
    },
    {
      id: 'cloud',
      title: 'Cloud Solutions',
      description: 'Accelerate your cloud journey with scalable, secure, and cost-effective cloud infrastructure.',
      icon: Cloud,
      color: 'from-sky-500 to-cyan-600',
      features: ['Cloud Migration', 'Multi-Cloud Strategy', 'Cloud Security', 'Cost Optimization']
    },
    {
      id: 'digital',
      title: 'Digital Transformation',
      description: 'Transform your business with cutting-edge digital technologies and innovative strategies.',
      icon: Zap,
      color: 'from-purple-500 to-violet-600',
      features: ['Process Digitization', 'Data Analytics', 'AI/ML Integration', 'Customer Experience']
    }
  ];

  const benefits = [
    { icon: TrendingUp, title: 'Increased Efficiency', desc: 'Streamline operations and reduce manual processes' },
    { icon: Shield, title: 'Enhanced Security', desc: 'Enterprise-grade security for your data' },
    { icon: Users, title: 'Better Collaboration', desc: 'Enable seamless teamwork across departments' },
    { icon: Layers, title: 'Scalable Architecture', desc: 'Solutions that grow with your business' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                <Globe className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-medium text-gray-700">Business Solutions</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Tailored
                <span className="bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x"> Solutions</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
                End-to-end business solutions designed to address your unique challenges and drive sustainable growth.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
              >
                Discuss Your Needs
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-orange-50 text-[#ff6b35] rounded-full text-sm font-semibold mb-4">
                Our Solutions
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Comprehensive Business Solutions
              </h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {solutions.map((solution, index) => (
                <Link 
                  key={solution.id}
                  to={`/solutions/${solution.id}`}
                  className="reveal-scale group"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="bento-card h-full hover:border-[#ff6b35]/30">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      <solution.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#ff6b35] transition-colors">
                      {solution.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-6">
                      {solution.description}
                    </p>

                    <ul className="space-y-3 mb-6">
                      {solution.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-3 text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="pt-4 border-t border-gray-100">
                      <span className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold group-hover:gap-3 transition-all">
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <span className="inline-block px-4 py-2 bg-white text-[#ff6b35] rounded-full text-sm font-semibold shadow-sm mb-4">
                Benefits
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why Choose Our Solutions
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={benefit.title}
                  className="reveal-scale bento-card text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-[#ff6b35]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-[#ff6b35] mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 reveal">
              Need a Custom Solution?
            </h2>
            <p className="text-gray-400 text-lg mb-10 reveal">
              Every business is unique. Let's create a solution tailored to your specific needs.
            </p>
            <Link 
              to="/contact"
              className="reveal inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default SolutionsPage;
